import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  KeyRound,
  Pause,
  Play,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Bot,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { useAuthStore } from '../../../stores/auth-store.js';
import { superUsersApi, type SuperUserRow, type SyncStatus } from '../api.js';

export function SuperUsersPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState<SuperUserRow | null>(null);
  const [deleteUser, setDeleteUser] = useState<SuperUserRow | null>(null);
  const [syncDetailUser, setSyncDetailUser] = useState<SuperUserRow | null>(null);
  const [mcpUser, setMcpUser] = useState<SuperUserRow | null>(null);
  const currentUser = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ['super-users'],
    queryFn: () => superUsersApi.list(),
  });

  const users = usersQuery.data ?? [];

  // Sync status batch — uma query por user. Carrega só pra users com TOTP setup.
  const syncQueries = useQueries({
    queries: users.map((u) => ({
      queryKey: ['super-user-sync', u.id],
      queryFn: () => superUsersApi.syncStatus(u.id),
      enabled: u.totp_confirmed,
      staleTime: 30_000,
    })),
  });

  const handleDelete = (u: SuperUserRow): void => {
    if (u.id === currentUser?.id) {
      toast.error('Você não pode deletar a si mesmo');
      return;
    }
    setDeleteUser(u);
  };

  const toggleMut = useMutation({
    mutationFn: async ({ u, newStatus }: { u: SuperUserRow; newStatus: 'active' | 'inactive' }) =>
      superUsersApi.patch(u.id, { status: newStatus }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['super-users'] });
      void qc.invalidateQueries({ queryKey: ['super-user-sync', vars.u.id] });
      toast.success(
        vars.newStatus === 'active'
          ? `"${vars.u.email}" reativado`
          : `"${vars.u.email}" desativado`,
      );
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const resyncMut = useMutation({
    mutationFn: async (id: number) => superUsersApi.resync(id),
    onSuccess: (data, id) => {
      void qc.invalidateQueries({ queryKey: ['super-user-sync', id] });
      toast.success('Re-sync disparado', {
        description: `${data.enqueued} servidor(es) → ${data.succeeded} OK, ${data.failed} falhou`,
      });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0f172a]">Super Users</h1>
          <p className="text-sm text-[#64748b] mt-0.5">
            Administradores do super-painel central. Acesso global a todos os servidores cadastrados.
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus size={14} /> Adicionar Super-Admin
        </Button>
      </div>

      <Card className="overflow-auto p-0">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="sticky top-0 z-10 bg-[#f8fafc] border-b border-[#e2e8f0] text-left">
            <tr>
              <th className="px-4 py-3 font-semibold text-[#374151]">Nome / Email</th>
              <th className="px-4 py-3 font-semibold text-[#374151] whitespace-nowrap">Status</th>
              <th className="px-4 py-3 font-semibold text-[#374151] whitespace-nowrap">TOTP</th>
              <th className="px-4 py-3 font-semibold text-[#374151] whitespace-nowrap">Sincronizado</th>
              <th className="px-4 py-3 font-semibold text-[#374151] whitespace-nowrap">Último login</th>
              <th className="px-4 py-3 font-semibold text-[#374151] whitespace-nowrap">Token MCP</th>
              <th className="px-4 py-3 font-semibold text-[#374151] text-right whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-[#94a3b8]">
                  Carregando...
                </td>
              </tr>
            )}
            {users.map((u, idx) => {
              const syncData = syncQueries[idx]?.data;
              const isMe = u.id === currentUser?.id;
              return (
                <tr
                  key={u.id}
                  className="group border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc] transition-colors"
                >
                  <td className="px-4 py-3.5">
                    <div className="font-medium text-[#0f172a]">
                      {u.name}
                      {isMe && (
                        <span className="ml-2 text-[10px] font-semibold text-[#0a4ee3] uppercase tracking-wider">
                          você
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#94a3b8] font-mono">{u.email}</div>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span
                      className={
                        u.status === 'active'
                          ? 'inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700'
                          : 'inline-flex px-2 py-0.5 rounded text-xs font-medium bg-[#f1f5f9] text-[#64748b]'
                      }
                    >
                      {u.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    {u.totp_confirmed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                        <ShieldCheck size={12} /> Configurado
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-orange-700">Pendente</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <SyncBadge
                      data={syncData}
                      loading={syncQueries[idx]?.isLoading}
                      totpReady={u.totp_confirmed}
                      onClick={() => setSyncDetailUser(u)}
                    />
                  </td>
                  <td className="px-4 py-3.5 text-[#64748b] text-xs whitespace-nowrap">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setMcpUser(u)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-2 py-1 text-xs font-medium text-[#374151] hover:bg-[#f8fafc]"
                      title={u.mcp_token ? 'Ver / regenerar / revogar' : 'Gerar token do MCP'}
                    >
                      <Bot size={12} className={u.mcp_token ? 'text-emerald-600' : 'text-[#94a3b8]'} />
                      {u.mcp_token ? <span className="font-mono">{u.mcp_token.prefixo}…</span> : 'Gerar'}
                    </button>
                  </td>
                  <td className="px-4 py-3.5 text-right whitespace-nowrap">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setResetUser(u)}
                        title="Resetar senha"
                      >
                        <KeyRound size={12} />
                        Senha
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resyncMut.mutate(u.id)}
                        loading={resyncMut.isPending && resyncMut.variables === u.id}
                        title="Forçar re-sincronização"
                        disabled={!u.totp_confirmed}
                      >
                        <RefreshCw size={12} />
                      </Button>
                      {!isMe && (
                        <>
                          <Button
                            variant={u.status === 'active' ? 'outline' : 'primary'}
                            size="sm"
                            onClick={() =>
                              toggleMut.mutate({
                                u,
                                newStatus: u.status === 'active' ? 'inactive' : 'active',
                              })
                            }
                            loading={toggleMut.isPending && toggleMut.variables?.u.id === u.id}
                          >
                            {u.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                          </Button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(u)}
                            className="p-1.5 rounded-lg text-[#94a3b8] hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Remover"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <CreateSuperUserModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />
      {deleteUser && <DeleteSuperUserModal user={deleteUser} onClose={() => setDeleteUser(null)} />}
      <SyncDetailModal user={syncDetailUser} onClose={() => setSyncDetailUser(null)} />
      <McpTokenModal user={mcpUser} onClose={() => setMcpUser(null)} />
    </div>
  );
}

/* ─── Badge de status de sync ──────────────────────────────────── */

function SyncBadge({
  data,
  loading,
  totpReady,
  onClick,
}: {
  data: SyncStatus | undefined;
  loading: boolean | undefined;
  totpReady: boolean;
  onClick: () => void;
}) {
  if (!totpReady) {
    return <span className="text-xs text-[#94a3b8]">—</span>;
  }
  if (loading || !data) {
    return <span className="text-xs text-[#94a3b8]">...</span>;
  }
  if (data.total === 0) {
    return <span className="text-xs text-[#94a3b8]">Sem servidores</span>;
  }
  const allOk = data.success === data.total && data.failed === 0;
  const hasFailure = data.failed > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
        allOk
          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          : hasFailure
            ? 'bg-red-50 text-red-600 hover:bg-red-100'
            : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
      }`}
      title="Ver detalhes de sincronização"
    >
      {allOk ? (
        <CheckCircle2 size={12} />
      ) : hasFailure ? (
        <AlertTriangle size={12} />
      ) : (
        <Clock size={12} />
      )}
      {data.success}/{data.total}
    </button>
  );
}

/* ─── Modal de detalhe de sync ─────────────────────────────────── */

function DeleteSuperUserModal({ user, onClose }: { user: SuperUserRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmacao, setConfirmacao] = useState('');

  const mut = useMutation({
    mutationFn: () => superUsersApi.delete(user.id),
    onSuccess: () => {
      toast.success('Super-admin removido');
      void qc.invalidateQueries({ queryKey: ['super-users'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  return (
    <Modal open onClose={onClose} title="Apagar super-admin" size="md">
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (confirmacao === 'APAGAR') mut.mutate();
        }}
        className="space-y-4"
      >
        <p className="text-xs text-[#94a3b8]">{user.email} — ação irreversível</p>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
          <p className="font-semibold mb-1">O que acontece:</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>Linha removida de super_users (sem recuperação)</li>
            <li>Removido das credenciais de suporte dos servidores cadastrados</li>
            <li>Sessões do super invalidadas</li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-[#0f172a]">
            Pra confirmar, digite{' '}
            <code className="bg-[#f1f5f9] px-1 rounded text-[#dc2626]">APAGAR</code>:
          </label>
          <input
            type="text"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            autoFocus
            className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm font-medium text-[#64748b] hover:text-[#0f172a] rounded-lg hover:bg-[#f1f5f9] disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mut.isPending || confirmacao !== 'APAGAR'}
            className="px-5 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-50"
          >
            {mut.isPending ? 'Apagando...' : 'Apagar definitivamente'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SyncDetailModal({ user, onClose }: { user: SuperUserRow | null; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['super-user-sync', user?.id],
    queryFn: () => (user ? superUsersApi.syncStatus(user.id) : Promise.resolve(null)),
    enabled: !!user,
  });

  if (!user) return null;

  return (
    <Modal open={!!user} onClose={onClose} title={`Sincronização — ${user.name}`} width={620}>
      {detailQuery.isLoading && (
        <div className="py-12 text-center text-sm text-[#94a3b8]">Carregando...</div>
      )}
      {detailQuery.data && (
        <>
          <div className="mb-4 grid grid-cols-3 gap-3">
            <StatusTile label="Sincronizados" count={detailQuery.data.success} variant="success" />
            <StatusTile label="Pendentes" count={detailQuery.data.pending} variant="warning" />
            <StatusTile label="Falharam" count={detailQuery.data.failed} variant="danger" />
          </div>
          <div className="overflow-hidden rounded-xl border border-[#e2e8f0]">
            <table className="w-full text-sm">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0] text-left">
                <tr>
                  <th className="px-3 py-2.5 font-semibold text-[#374151]">Servidor</th>
                  <th className="px-3 py-2.5 font-semibold text-[#374151]">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-[#374151] whitespace-nowrap">
                    Tentativas
                  </th>
                  <th className="px-3 py-2.5 font-semibold text-[#374151] whitespace-nowrap">
                    Último try
                  </th>
                </tr>
              </thead>
              <tbody>
                {detailQuery.data.details.map((d) => (
                  <tr
                    key={d.server_id}
                    className="border-b border-[#f1f5f9] last:border-0 align-top hover:bg-[#f8fafc] transition-colors"
                  >
                    <td className="px-3 py-2.5 font-medium text-[#0f172a]">{d.server_name}</td>
                    <td className="px-3 py-2.5">
                      <StatusPill status={d.status} />
                      {d.last_error && (
                        <div className="mt-1 max-w-[260px] text-[10px] text-red-600 font-mono whitespace-pre-wrap break-all">
                          {d.last_error.slice(0, 200)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-[#64748b] tabular-nums">{d.attempts}</td>
                    <td className="px-3 py-2.5 text-xs text-[#94a3b8]">
                      {d.last_attempt_at
                        ? new Date(d.last_attempt_at).toLocaleString('pt-BR')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

function StatusTile({
  label,
  count,
  variant,
}: {
  label: string;
  count: number;
  variant: 'success' | 'warning' | 'danger';
}) {
  const colorByVariant: Record<typeof variant, string> = {
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    warning: 'border-orange-200 bg-orange-50 text-orange-700',
    danger: 'border-red-200 bg-red-50 text-red-600',
  };
  return (
    <div className={`rounded-xl border p-3 ${colorByVariant[variant]}`}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-80">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{count}</div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const labels: Record<string, string> = {
    success: 'OK',
    pending: 'Pendente',
    in_progress: 'Processando',
    failed: 'Falhou',
    never_synced: 'Nunca',
  };
  const classes: Record<string, string> = {
    success: 'bg-emerald-50 text-emerald-700',
    pending: 'bg-orange-50 text-orange-700',
    in_progress: 'bg-[#eff4ff] text-[#0a4ee3]',
    failed: 'bg-red-50 text-red-600',
    never_synced: 'bg-[#f1f5f9] text-[#64748b]',
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase ${classes[status] ?? 'bg-[#f1f5f9] text-[#64748b]'}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

/* ─── Modal criar super-admin ───────────────────────────────────── */

function CreateSuperUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const qc = useQueryClient();

  const reset = (): void => {
    setName('');
    setEmail('');
    setPassword('');
  };

  const mut = useMutation({
    mutationFn: async () => superUsersApi.create({ name, email, password }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super-users'] });
      toast.success('Super-admin criado', {
        description: 'No primeiro login será forçado o setup TOTP. Depois a credencial será replicada nos servidores.',
      });
      reset();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Adicionar Super-Admin" width={480}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <Input
          label="Nome"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <Input
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
          hint="Email do admin do super-painel"
        />
        <Input
          label="Senha"
          type="text"
          required
          minLength={8}
          maxLength={120}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="No mínimo 8 caracteres. Passe ao novo admin pelo canal seguro."
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mut.isPending}>
            Adicionar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Modal resetar senha ────────────────────────────────────────── */

function ResetPasswordModal({
  user,
  onClose,
}: {
  user: SuperUserRow | null;
  onClose: () => void;
}) {
  const [custom, setCustom] = useState('');
  const [usarCustom, setUsarCustom] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const qc = useQueryClient();

  const close = (): void => {
    setCustom('');
    setUsarCustom(false);
    setErro(null);
    setResult(null);
    setCopied(false);
    onClose();
  };

  const mut = useMutation({
    mutationFn: async (password: string | undefined): Promise<string | undefined> => {
      if (!user) return undefined;
      const res = await superUsersApi.resetPassword(user.id, password);
      return res.tempPassword;
    },
    onSuccess: (tempPassword) => {
      void qc.invalidateQueries({ queryKey: ['super-user-sync', user?.id] });
      if (tempPassword) setResult(tempPassword);
      toast.success(`Senha de "${user?.email}" resetada`, {
        description: 'Replicada nos servidores. Repasse pelo canal seguro.',
      });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  function submit(e: FormEvent): void {
    e.preventDefault();
    setErro(null);
    if (usarCustom) {
      if (custom.length < 8) {
        setErro('Senha customizada deve ter no mínimo 8 caracteres');
        return;
      }
      mut.mutate(custom);
    } else {
      mut.mutate(undefined);
    }
  }

  return (
    <Modal open={!!user} onClose={close} title="Resetar senha" width={440}>
      {result ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[#0f172a]">
            Senha de <strong>{user?.email}</strong> (repasse pelo canal seguro):
          </p>
          <div className="flex items-center gap-2 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg px-3 py-2">
            <code className="flex-1 text-sm font-mono text-[#0f172a] select-all break-all">{result}</code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(result);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              className="shrink-0 px-3 py-1.5 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#64748b] hover:bg-[#f1f5f9]"
            >
              {copied ? 'Copiado!' : 'Copiar'}
            </button>
          </div>
          <p className="text-xs text-[#94a3b8]">Já replicada nos servidores cadastrados.</p>
          <Button onClick={close} className="w-full">
            Fechar
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <p className="-mt-1 text-xs text-[#94a3b8]">
            Usuário: <span className="text-[#0f172a]">{user?.email}</span>
          </p>

          <div className="bg-[#fef9c3]/50 border border-[#fde047] rounded-lg p-3 text-xs text-[#713f12]">
            <p className="font-semibold mb-1">Atenção</p>
            <p>
              Resetar a senha desloga o usuário de todos os dispositivos imediatamente. A nova senha é
              replicada nos servidores cadastrados.
            </p>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={usarCustom}
                onChange={(e) => setUsarCustom(e.target.checked)}
                className="rounded"
              />
              Definir senha customizada (em vez de gerar aleatória)
            </label>
            {usarCustom && (
              <input
                type="text"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="mínimo 8 caracteres"
                autoFocus
                className="w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a4ee3]/20 focus:border-[#0a4ee3]"
              />
            )}
          </div>

          {erro && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-3">{erro}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={close} disabled={mut.isPending}>
              Cancelar
            </Button>
            <Button type="submit" loading={mut.isPending}>
              Resetar senha
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/* ─── Token do MCP Metrics (1 por pessoa, URL única, N servidores) ──────── */

function McpTokenModal({ user, onClose }: { user: SuperUserRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [novo, setNovo] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  const gerar = useMutation({
    mutationFn: (id: number) => superUsersApi.createMcpToken(id),
    onSuccess: (r) => {
      setNovo(r.token);
      setCopiado(false);
      void qc.invalidateQueries({ queryKey: ['super-users'] });
      toast.success('Token gerado e replicado pros servidores');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao gerar token'),
  });
  const revogar = useMutation({
    mutationFn: (id: number) => superUsersApi.revokeMcpToken(id),
    onSuccess: () => {
      setNovo(null);
      void qc.invalidateQueries({ queryKey: ['super-users'] });
      toast.success('Token revogado em todos os servidores');
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Falha ao revogar'),
  });

  function fechar() {
    setNovo(null);
    onClose();
  }
  async function copiar() {
    if (!novo) return;
    try {
      await navigator.clipboard.writeText(novo);
      setCopiado(true);
    } catch {
      /* clipboard bloqueado — o texto é selecionável */
    }
  }

  if (!user) return null;
  const tem = !!user.mcp_token;
  return (
    <Modal open={!!user} onClose={fechar} title={`Token do MCP Metrics · ${user.name}`}>
      {novo ? (
        <>
          <p className="text-sm text-[#0f172a]">
            Copie agora — o token <strong>não será mostrado de novo</strong>. Ele já foi replicado pra todos os servidores
            e vale em todos com uma única URL.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 select-all break-all rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-xs text-[#0f172a]">
              {novo}
            </code>
            <Button variant="outline" size="sm" onClick={() => void copiar()}>
              {copiado ? 'Copiado ✓' : 'Copiar'}
            </Button>
          </div>
          <div className="mt-4 rounded-lg bg-[#f8fafc] p-3 text-xs text-[#64748b]">
            <p className="font-medium text-[#0f172a]">Como conectar</p>
            <p className="mt-1">
              <strong>claude.ai / Claude Desktop:</strong> Settings → Connectors → Add custom connector → URL{' '}
              <code>https://mcp.metricsfarma.cloud/mcp</code> → Connect → colar o token.
            </p>
            <p className="mt-1">
              <strong>Claude Code:</strong>{' '}
              <code>claude mcp add --transport http metrics https://mcp.metricsfarma.cloud/mcp --header "Authorization: Bearer &lt;token&gt;"</code>
            </p>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={fechar}>Fechar</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[#0f172a]">
            {tem ? (
              <>
                Token ativo <span className="font-mono">{user.mcp_token?.prefixo}…</span>
                {user.mcp_token?.criado_em ? ` (gerado em ${new Date(user.mcp_token.criado_em).toLocaleString('pt-BR')})` : ''}.
                Gerar de novo <strong>substitui</strong> o atual em todos os servidores.
              </>
            ) : (
              <>
                {user.name} ainda não tem token do MCP. O token é <strong>um por pessoa</strong>, vale em todos os servidores
                (URL única) e é replicado pelo mesmo sync dos super-usuários.
              </>
            )}
          </p>
          {!user.totp_confirmed && (
            <p className="mt-2 text-xs text-orange-700">
              Este usuário ainda não configurou o TOTP — o sync pros servidores depende disso. Peça pra ele fazer o 1º login.
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            {tem && (
              <Button variant="danger" loading={revogar.isPending} onClick={() => revogar.mutate(user.id)}>
                Revogar
              </Button>
            )}
            <Button loading={gerar.isPending} disabled={!user.totp_confirmed} onClick={() => gerar.mutate(user.id)}>
              {tem ? 'Gerar novo' : 'Gerar token'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
